import { get } from 'svelte/store';

import { downloadPDFReport, generatePDFReportURL } from '@/teoCore/report/generate-pdf-report';
import type { TeoCore } from '@/teoCore/TeoCore';
import { useCoreStateAnalysisUsageQuotaInfo } from '@/ui/state/use-core-state.svelte';
import { useTeoCore } from '@/utils/teo-context';

async function getReportData(core: TeoCore) {
  const activeText = core.activeText;
  const analysis = activeText.analysis;
  return {
    title: `TEO Report - ${activeText.ui.state.title}`,
    indices: analysis?.data.indices,
    configuration: activeText.analysisConfiguration,
    configSetup: await activeText.core.getTextConfigOptions(),
    statistics: analysis?.statistics ?? {},
    text: activeText.text,
    tpi: analysis?.getTPI() ?? 0,
    tpiTarget: core.staticConfig.tpiTarget,
    reportId: analysis?.data.report_id ?? Math.random().toString(36).substring(2, 15),
    createdAt: analysis?.data.created_at ?? analysis?.createdAt ?? Date.now(),
  };
}

// TODO: extract duplicate code
async function downloadWithNotification(core: TeoCore) {
  const data = await getReportData(core);

  const donwloadAbortController = new AbortController();
  const notificationId = core.notifications.add({
    title: 'TEO Report',
    message: 'Der Report wird generiert...',
    mode: 'info',
    buttons: [
      {
        label: 'Abbrechen',
        onclick: () => {
          donwloadAbortController.abort();
        },
      },
    ],
  });
  try {
    await downloadPDFReport(data, donwloadAbortController.signal);
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return;
    }
    core.notifications.add({
      title: 'Fehler beim Generieren des Reports',
      message: 'Der Report konnte nicht generiert werden. Bitte versuchen Sie es erneut.',
      mode: 'error',
    });
  } finally {
    core.notifications.close(notificationId);
  }
}

async function generateReportURLWithNotification(core: TeoCore) {
  const data = await getReportData(core);

  const generateAbortController = new AbortController();
  const notificationId = core.notifications.add({
    title: 'TEO Report',
    message: 'Der Report wird generiert...',
    mode: 'info',
    buttons: [
      {
        label: 'Abbrechen',
        onclick: () => {
          generateAbortController.abort();
        },
      },
    ],
  });
  try {
    return generatePDFReportURL(data, generateAbortController.signal);
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return null;
    }
    core.notifications.add({
      title: 'Fehler beim Generieren des Reports',
      message: 'Der Report konnte nicht generiert werden. Bitte versuchen Sie es erneut.',
      mode: 'error',
    });
  } finally {
    core.notifications.close(notificationId);
  }
  return null;
}

export function downloadReport(teoCore: TeoCore) {
  const activeText = teoCore.activeText;
  const analysisState = activeText.analysisState;
  const analysisQuotaInfo = get(useCoreStateAnalysisUsageQuotaInfo());

  if (!analysisState.isCurrent) {
    const notificationNeedsAnalysis = teoCore.notifications.add({
      title: 'TEO Report',
      message: 'Es muss erst eine Analyse durchgeführt werden.',
      mode: 'warning',
      buttons: [
        {
          label: 'Abbrechen',
          variant: 'secondary',
          onclick: () => {
            teoCore.notifications.close(notificationNeedsAnalysis);
          },
        },
        {
          label: `Analyse starten ${analysisQuotaInfo?.text ?? ''}`,
          variant: 'primary',
          disabled: analysisQuotaInfo?.depleted ?? false,
          onclick: () => {
            teoCore.activeText
              .analyze({ trigger: 'button_click' })
              .then(() => {
                teoCore.notifications.close(notificationNeedsAnalysis);
                return downloadWithNotification(teoCore);
              })
              .catch(() => {
                teoCore.notifications.close(notificationNeedsAnalysis);
              });
            return 'keep';
          },
        },
      ],
    });
  } else {
    return downloadWithNotification(teoCore);
  }
}

async function generateReportURL(teoCore: TeoCore): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const activeText = teoCore.activeText;
    const analysisState = activeText.analysisState;
    const analysisQuotaInfo = get(useCoreStateAnalysisUsageQuotaInfo());

    if (!analysisState.isCurrent) {
      const notificationNeedsAnalysis = teoCore.notifications.add({
        title: 'TEO Report',
        message: 'Es muss erst eine Analyse durchgeführt werden.',
        mode: 'warning',
        buttons: [
          {
            label: 'Abbrechen',
            variant: 'secondary',
            onclick: () => {
              teoCore.notifications.close(notificationNeedsAnalysis);
              resolve(null);
            },
          },
          {
            label: `Analyse starten ${analysisQuotaInfo?.text ?? ''}`,
            variant: 'primary',
            disabled: analysisQuotaInfo?.depleted ?? false,
            onclick: () => {
              teoCore.activeText
                .analyze({ trigger: 'button_click' })
                .then(() => {
                  teoCore.notifications.close(notificationNeedsAnalysis);
                  return generateReportURLWithNotification(teoCore)
                    .then(resolve)
                    .catch((error) => {
                      teoCore.notifications.close(notificationNeedsAnalysis);
                      reject(error);
                    });
                })
                .catch(() => {
                  teoCore.notifications.close(notificationNeedsAnalysis);
                });
              return 'keep';
            },
          },
        ],
      });
    } else {
      generateReportURLWithNotification(teoCore).then(resolve).catch(reject);
    }
  });
}

export function useGenerateReportURL() {
  const teoCore = useTeoCore();
  return () => generateReportURL(teoCore);
}

export function useDownloadReport() {
  const teoCore = useTeoCore();
  return () => downloadReport(teoCore);
}
